import type { Feature, FeatureCollection, Point } from "geojson";
import { type ComponentType, useEffect, useMemo, useRef, useState } from "react";
import { unitShown } from "./maneuverUnits";

import type { DeployedUnit, Deployment, Phase, SubordinateUnit } from "../../api/types";
import { useUi } from "../../state/ui";
import { useTicker } from "../manoeuvre/motion";
import { useMapRevealed } from "../reveal";
import { DATA_TRANSITION_MS, lerpPoint, smoothstep, startDataTransition, syncReveal, usePhaseClock } from "./phaseTransition";
import { symbolId } from "./symbols";
import { defensiveRoles, roleShort } from "./propagation";
import { useShownDeployment } from "../manoeuvre/useOrientation";
import { entityMemory, type EntityInput, GHOST_OPACITY, GHOST_SIZE, stepEntities } from "./unitEntities";
import { rootShift, useZoomLevels } from "./zoomLevels";
import { VisibilityRings } from "../visibility/VisibilityLayers";

export { ENTITY_ENTER_MS, type EntityFade, fadeAt, fadeDone, LEVEL_SLIDE_MS } from "./unitEntities";

/** Couches du dispositif : symboles APP-6D des unités au temps affiché, désignations portées par le symbole
 *  comme sur l'iPad (pas de texte sous l'unité : la mission se lit par son symbole). Pas de trait de bond
 *  depuis le temps précédent : l'iPad n'en dessine pas (choix d'Alexis du 28/09/2026) ; d'un temps à l'autre,
 *  les unités glissent jusqu'à leur nouvelle position (phaseTransition). Les identifiants de couches
 *  commencent par « units- ».
 *
 *  Opacité par entité (docs/14 §2.10), calculée par l'horloge `requestAnimationFrame` et non par les transitions
 *  du moteur (sans effet sur une propriété pilotée par les données) : `icon-opacity: ["get", "opacity"]`, qui
 *  porte le facteur d'ouverture du dossier (fondu de 900 ms) ; une unité qui apparaît se fond, une unité masquée
 *  ou retirée reste `MAP_EXIT_MS` à opacité décroissante avant de quitter la collection. À chaque réponse du
 *  serveur qui change le dispositif à temps égal (mission, fuseau, ancrage, rôles, annulation), unités et symboles
 *  glissent de ce qu'ils affichent vers la réponse (`startDataTransition`, 900 ms). */

export const UNIT_LAYER_ID = "units-symbol";
/** Unités des niveaux inférieurs de la carte zoomée (registre J11) : lecture seule (fiche), ni glissées ni menu. */
export const SUBUNIT_LAYER_ID = "units-sub-symbol";

/** Repère discret (registre J13) : opacité et taille du symbole selon `ghost` (0 plein, 1 repère). */
const GHOST_OPACITY_EXPR = ["-", 1, ["*", 1 - GHOST_OPACITY, ["coalesce", ["get", "ghost"], 0]]];
const ICON_OPACITY = ["*", ["get", "opacity"], GHOST_OPACITY_EXPR];
/** Taille : repère discret (plus petit), croissance d'un subordonné qui éclate de son chef et décroissance de celui-ci
 *  (registre J16, `size`). */
const ICON_SIZE = ["*", ["coalesce", ["get", "size"], 1], ["-", 1, ["*", 1 - GHOST_SIZE, ["coalesce", ["get", "ghost"], 0]]]];

/** Composants Source/Layer du moteur chargé (Mapbox GL JS ou MapLibre GL JS), typés au plus large :
 *  les styles sont écrits dans le sous-ensemble commun aux deux moteurs. */
interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
}

/** Ce qui identifie une réponse du serveur sur le même dispositif : dossier, camp, OPO et version du dossier. */
export interface DataSignature {
  exercise: string | null;
  map: string;
  opo: number | null;
  version: number | null;
}

export function dataSignature(data: Deployment, exercise: string | null): DataSignature {
  return { exercise, map: data.map_slug, opo: data.centac?.opo ?? null, version: data.exercise_version };
}

/** Nouvelle version du même dossier (écriture, annulation, rechargement) : unités et symboles glissent vers la
 *  réponse. Un autre dossier, un autre camp ou un aperçu du brouillon (dossier non enregistré) : non. */
export function isDataTransition(previous: DataSignature | null, next: DataSignature): boolean {
  return Boolean(
    previous &&
      next.exercise &&
      previous.exercise === next.exercise &&
      previous.map === next.map &&
      previous.opo === next.opo &&
      previous.version !== next.version,
  );
}

/** La réponse diffère de la dernière relevée (dossier, camp, OPO en CENTAC ou version) : la signature est mise à
 *  jour, et `isDataTransition` dit s'il faut glisser. */
export function signatureChanged(previous: DataSignature | null, next: DataSignature): boolean {
  return (
    previous === null ||
    previous.version !== next.version ||
    previous.exercise !== next.exercise ||
    previous.map !== next.map ||
    previous.opo !== next.opo
  );
}

/** Unité montrée : du dispositif du dossier, ou d'un niveau inférieur de la carte zoomée. */
type Shown = DeployedUnit | SubordinateUnit;

function isSub(unit: Shown): unit is SubordinateUnit {
  return "source" in unit && unit.source !== "dossier";
}

/** Unités à montrer : à la profondeur 0, celles du dispositif ; plus bas, celles de la profondeur, les unités gardées
 *  du dossier (source « dossier ») étant reprises du dispositif à jour (déplacements depuis le calcul compris).
 *  Une profondeur ne montre que ses unités (registre J13) : celles des profondeurs précédentes n'y figurent pas, sauf
 *  PC, TC et formations que rien ne détaille (repère discret, `ghostKeys`). */
export function shownUnits(deployed: readonly DeployedUnit[], level: readonly SubordinateUnit[] | null): Shown[] {
  if (!level) return [...deployed];
  const current = new Map(deployed.map((u) => [u.key, u]));
  return level.map((u) => (u.source === "dossier" ? (current.get(u.key) ?? u) : u));
}

/** Empreinte des positions d'une profondeur (clés et positions à chaque temps) : une nouvelle réponse du serveur à
 *  la même profondeur (calcul refait après un déplacement, une retouche, un terrain prêt) qui la change fait
 *  glisser les subordonnés vers leurs nouvelles positions au lieu de les y faire sauter (registre J14) ; une
 *  réponse identique (autre requête, même contenu) ne fait rien. */
export function levelSignature(level: readonly SubordinateUnit[] | null): string {
  if (!level) return "";
  return level
    .map((u) => {
      const at = Object.entries(u.positions)
        .map(([ph, p]) => (p ? `${ph}${p.lon.toFixed(6)},${p.lat.toFixed(6)}` : ""))
        .join(";");
      return `${u.key}:${at}`;
    })
    .join("|");
}

/** Formations plus hautes que la profondeur affichée que rien ne détaille : un repère discret, pas le symbole plein
 *  au milieu des unités plus basses (registre J13). */
export function ghostKeys(level: readonly SubordinateUnit[] | null): Set<string> {
  return new Set((level ?? []).filter((u) => u.undetailed).map((u) => u.key));
}

/** Position [lon, lat] d'une unité au temps `phase` : celle de sa carte, et pour un subordonné, décalée du
 *  déplacement de l'unité du dossier dont il descend depuis le calcul (`roots`). */
export function unitTarget(
  unit: Shown,
  phase: Phase,
  roots: Record<string, Partial<Record<Phase, [number, number]>>>,
  rootTargets: Map<string, [number, number]>,
): [number, number] | null {
  const pos = unit.positions[phase];
  if (!pos) return null;
  if (!isSub(unit)) return [pos.lon, pos.lat];
  const [dx, dy] = rootShift(roots, unit.root, phase, rootTargets.get(unit.root));
  return [pos.lon + dx, pos.lat + dy];
}

export function UnitsLayers({ Source, Layer }: EngineParts) {
  const { data, isPlaceholderData } = useShownDeployment(); // aperçu du sens de la manœuvre (docs/14 §2.14)
  const phase = useUi((s) => s.phase);
  const visible = useUi((s) => s.unitsVisible);
  const supportsVisible = useUi((s) => s.supportUnitsVisible);
  const roleLabels = useUi((s) => s.roleLabelsVisible);
  const selection = useUi((s) => s.selection);
  const dragPreview = useUi((s) => s.dragPreview);
  const exerciseId = useUi((s) => s.deploymentParams?.exercise_id ?? null);
  const selectedCode = selection?.layer === "unit" ? selection.code : "";
  const selectedSub = selection?.layer === "subunit" ? selection.key : "";
  const level = useZoomLevels((s) => s.units);
  const depth = useZoomLevels((s) => s.depth);
  const roots = useZoomLevels((s) => s.roots);
  const levelSig = useMemo(() => levelSignature(level), [level]);
  const lastLevel = useRef<{ depth: number; sig: string }>({ depth, sig: levelSig });
  const revealed = useMapRevealed();
  // Transition d'un temps à l'autre : chaque unité part de la position affichée au moment du changement.
  const { id, progress } = usePhaseClock();
  const reveal = usePhaseClock((s) => s.reveal);
  const memory = useRef(entityMemory());
  const start = useRef({ id: -1, positions: new Map<string, [number, number]>() });
  const signature = useRef<DataSignature | null>(null);
  // Réponse du serveur arrivée, transition pas encore ouverte (elle part dans l'effet qui suit) : les unités
  // restent où elles sont jusque-là, pour glisser ensuite depuis cette position.
  const held = useRef<{ id: number; positions: Map<string, [number, number]> } | null>(null);
  const [ticking, setTicking] = useState(false);
  const tick = useTicker(ticking);

  // Fondu d'ouverture du dossier : facteur `reveal` de l'horloge commune, multiplié dans l'opacité.
  useEffect(() => syncReveal(revealed), [revealed]);

  const next = data && !isPlaceholderData ? dataSignature(data, exerciseId) : null;
  const pending = next !== null && isDataTransition(signature.current, next);
  if (next && signatureChanged(signature.current, next)) {
    if (pending) held.current = { id, positions: new Map([...memory.current.displayed].map(([k, v]) => [k, v.at])) };
    signature.current = next;
  }
  useEffect(() => {
    if (held.current && held.current.id === usePhaseClock.getState().id) startDataTransition(DATA_TRANSITION_MS, useUi.getState().phase);
  }, [next?.version, next?.exercise, next?.map, next?.opo]); // eslint-disable-line react-hooks/exhaustive-deps
  // Même profondeur, positions recalculées par le serveur : glissement depuis l'affichage (registre J14).
  const levelMoved = depth > 0 && lastLevel.current.depth === depth && lastLevel.current.sig !== levelSig && levelSig !== "";
  if (levelMoved && !held.current) held.current = { id, positions: new Map([...memory.current.displayed].map(([k, v]) => [k, v.at])) };
  lastLevel.current = { depth, sig: levelSig };
  useEffect(() => {
    if (held.current && held.current.id === usePhaseClock.getState().id) startDataTransition(DATA_TRANSITION_MS, useUi.getState().phase);
  }, [levelSig]); // eslint-disable-line react-hooks/exhaustive-deps
  if (held.current && held.current.id !== id) held.current = null;

  if (start.current.id !== id) {
    start.current = { id, positions: new Map([...memory.current.displayed].map(([k, v]) => [k, v.at])) };
  }
  const now = ticking ? tick : performance.now();
  const eased = held.current ? 0 : smoothstep(progress);
  const origins = held.current?.positions ?? start.current.positions;
  const inputs: EntityInput[] = [];

  // Position visée de chaque unité du dispositif (glisser-déposer compris) : les subordonnés la suivent.
  const rootTargets = new Map<string, [number, number]>();
  for (const unit of data?.units ?? []) {
    const p = unit.positions[phase];
    if (!p) continue;
    const dragged = dragPreview && dragPreview.key === unit.key && dragPreview.phase === phase;
    rootTargets.set(unit.key, dragged ? dragPreview.lngLat : [p.lon, p.lat]);
  }
  const units = data ? shownUnits(data.units, level) : [];
  const ghosts = ghostKeys(level);
  // Rôles d'une défense (registre A126) : « 1er éch. déf. », « 2e éch. déf. », jamais « Reco ».
  const defensive = defensiveRoles(data);
  for (const unit of units) {
    const doctrinal = unit.positions[phase];
    if (!doctrinal || !visible[unit.side] || unit.hidden || !unitShown(unit, supportsVisible)) continue;
    const sub = isSub(unit);
    // Aperçu pendant un glisser-déposer : l'unité suit le pointeur jusqu'à l'enregistrement.
    const dragged = !sub && dragPreview && dragPreview.key === unit.key && dragPreview.phase === phase;
    const target = dragged ? dragPreview.lngLat : unitTarget(unit, phase, roots, rootTargets)!;
    const at = dragged || (sub && dragPreview?.key === unit.root) ? target : lerpPoint(origins.get(unit.key), target, eased);
    const effort = unit.effort.includes(phase);
    inputs.push({
      key: unit.key,
      at,
      sub,
      ghost: ghosts.has(unit.key),
      properties: {
        key: unit.key,
        moved: Boolean(dragged || ("moved" in doctrinal && doctrinal.moved)),
        code: unit.code,
        side: unit.side,
        sub,
        icon: symbolId(unit.sidc, unit.designation_left, unit.designation_right),
        // Repère du rôle tactique lu en profondeur (docs/25 §4), sous le symbole.
        role: !sub && roleLabels ? (roleShort((unit as DeployedUnit).tactical_role, unit.side, defensive) ?? "") : "",
        effort,
        sort: effort ? 0 : unit.side === "AMI" ? 1 : 2,
      },
    });
  }
  const { entities, animating } = stepEntities(memory.current, inputs, now);
  const points: Feature<Point>[] = entities.map((e) => ({
    type: "Feature",
    geometry: { type: "Point", coordinates: e.at },
    properties: {
      ...e.properties,
      opacity: e.opacity * reveal,
      ghost: e.ghost,
      ...(e.size !== 1 ? { size: Math.round(e.size * 1000) / 1000 } : {}),
      ...(e.leaving ? { leaving: true } : {}),
    },
  }));
  useEffect(() => {
    if (animating !== ticking) setTicking(animating);
  });

  if (!data) return null;
  const collection = { type: "FeatureCollection", features: points } as FeatureCollection;
  return (
    <>
      <Source id="gx-units" type="geojson" data={collection}>
        <VisibilityRings Layer={Layer} />
        <Layer
          id="units-moved"
          type="circle"
          filter={["==", ["get", "moved"], true]}
          paint={{
            "circle-radius": 27,
            "circle-color": "rgba(0,0,0,0)",
            "circle-stroke-color": "#c2410c",
            "circle-stroke-width": 1.4,
            "circle-stroke-opacity": ["*", 0.8, ["get", "opacity"]],
          }}
        />
        <Layer
          id="units-selected"
          type="circle"
          filter={[
            "any",
            ["all", ["!=", ["get", "sub"], true], ["==", ["get", "code"], selectedCode]],
            ["==", ["get", "key"], selectedSub],
          ]}
          paint={{
            "circle-radius": 31,
            "circle-color": "#2f6fed",
            "circle-opacity": ["*", 0.14, ["get", "opacity"]],
            "circle-stroke-color": "#2f6fed",
            "circle-stroke-width": 1.5,
            "circle-stroke-opacity": ["get", "opacity"],
          }}
        />
        <Layer
          id={UNIT_LAYER_ID}
          type="symbol"
          filter={["!=", ["get", "sub"], true]}
          layout={{
            "icon-image": ["get", "icon"],
            "icon-size": ICON_SIZE, // taille fixe à l'écran (repère discret : plus petit), comme sur l'iPad
            "icon-allow-overlap": true,
            "icon-ignore-placement": true,
            "symbol-sort-key": ["get", "sort"],
          }}
          paint={{ "icon-opacity": ICON_OPACITY }}
        />
        <Layer
          id="units-role"
          type="symbol"
          filter={["all", ["!=", ["get", "sub"], true], ["!=", ["get", "role"], ""]]}
          layout={{
            "text-field": ["get", "role"],
            "text-font": ["Open Sans Semibold"],
            "text-size": 10,
            "text-offset": [0, 2.35],
            "text-anchor": "top",
            "text-allow-overlap": true,
            "text-ignore-placement": true,
          }}
          paint={{
            "text-color": ["match", ["get", "side"], "ENI", "#b91c1c", "#1d4ed8"],
            "text-halo-color": "rgba(255,255,255,0.92)",
            "text-halo-width": 1.4,
            "text-opacity": ICON_OPACITY,
          }}
        />
        <Layer
          id={SUBUNIT_LAYER_ID}
          type="symbol"
          filter={["==", ["get", "sub"], true]}
          layout={{
            "icon-image": ["get", "icon"],
            "icon-size": ICON_SIZE,
            "icon-allow-overlap": true,
            "icon-ignore-placement": true,
            "symbol-sort-key": ["get", "sort"],
          }}
          paint={{ "icon-opacity": ICON_OPACITY }}
        />
      </Source>
    </>
  );
}
